import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format, isValid } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { buildAllocationCsvRows, rowsToCsv, buildAcMetaFromGroups, buildAcServiceTypesFromGroups, buildCostMap, buildRevenueMap, toApiCustomRules, toApiReverseRules } from '../helpers';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';
import {
  closePublishModal,
  setEffectiveFrom,
  setEffectiveUntil,
  openOtpModal,
  clearApprovalToken,
  clearUploadedAllocationCsv,
  setActiveTab,
} from '../../../../redux/d2cAllocationSlice';
import {
  usePublishAllocationMutation,
  useGetAllocationMerchantDetailQuery,
  useGetAllocationPreviewStatsQuery,
  useGetSupplyCostsQuery,
  useGetRevenueGridQuery,
  useGetAcMetaQuery,
  useGetMarginPreviewQuery,
} from '../../../../redux/allocationApiSlice';

// Redux stores the effective dates as `YYYY-MM-DDTHH:mm` local strings. The MUI
// picker works in Date objects, so convert in both directions and treat anything
// unparseable as cleared.
const STORE_FMT = "yyyy-MM-dd'T'HH:mm";
const toLocalDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isValid(d) ? d : null;
};
const toStoredString = (d) => (d && isValid(d) ? format(d, STORE_FMT) : '');
// The publish API expects ISO-8601 UTC (e.g. "2026-06-17T00:00:00Z"), so convert the
// local store string before sending. Returns null for empty/invalid.
const toApiIso = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isValid(d) ? d.toISOString() : null;
};

export default function PublishModal({ onDownloadCsv }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const open              = useSelector((s) => s.d2cAllocation.publishModal?.open || false);
  const activeMerchantId  = useSelector((s) => s.d2cAllocation.activeMerchantId);
  const effectiveFrom     = useSelector((s) => s.d2cAllocation.effectiveFrom);
  const effectiveUntil    = useSelector((s) => s.d2cAllocation.effectiveUntil);
  const approvalToken     = useSelector((s) => s.d2cAllocation.approvalToken);
  const forwardMode       = useSelector((s) => s.d2cAllocation.forwardMode);
  const autoServiceTypes  = useSelector((s) => s.d2cAllocation.autoServiceTypes);
  const acEnabled         = useSelector((s) => s.d2cAllocation.acEnabled);
  const groupSameParent   = useSelector((s) => s.d2cAllocation.groupSameParent);
  const customRules       = useSelector((s) => s.d2cAllocation.customRules);
  const reverseRules      = useSelector((s) => s.d2cAllocation.reverseRules);
  const rvpEnabled        = useSelector((s) => s.d2cAllocation.rvpEnabled);
  const includeLossMaking = useSelector((s) => s.d2cAllocation.includeLossMaking);
  const uploadedAllocationCsv = useSelector((s) => s.d2cAllocation.uploadedAllocationCsv);
  const uploadedAllocationCsvName = useSelector((s) => s.d2cAllocation.uploadedAllocationCsvName);

  const { data: merchantRaw } = useGetAllocationMerchantDetailQuery(activeMerchantId, {
    skip: !activeMerchantId,
  });
  const merchant = merchantRaw?.data ?? merchantRaw ?? null;

  // Authoritative OTP gate + loss count from the backend.
  const { data: previewStatsRaw } = useGetAllocationPreviewStatsQuery(activeMerchantId, {
    skip: !activeMerchantId || !open,
  });
  const previewStats = previewStatsRaw?.data ?? previewStatsRaw ?? null;

  const { data: groups = [] }         = useGetServiceabilityGroupsQuery();
  const { data: supplyCostsRaw = [] } = useGetSupplyCostsQuery();
  const { data: revenueGridRaw = [] } = useGetRevenueGridQuery(activeMerchantId, { skip: !activeMerchantId });
  const uploadedFwdRows = useSelector((s) => s.d2cAllocation.merchantFwdRateCardRows || []);
  const uploadedRvpRows = useSelector((s) => s.d2cAllocation.merchantRvpRateCardRows || []);

  const { data: acMetaFromApi = {} } = useGetAcMetaQuery();
  const acMeta         = useMemo(() => Object.keys(acMetaFromApi).length ? acMetaFromApi : buildAcMetaFromGroups(groups), [acMetaFromApi, groups]);
  const acServiceTypes = useMemo(() => buildAcServiceTypesFromGroups(groups), [groups]);
  const costMap        = useMemo(() => buildCostMap(supplyCostsRaw),          [supplyCostsRaw]);
  const effectiveRevenueRows = useMemo(
    () => (uploadedFwdRows.length + uploadedRvpRows.length) > 0 ? [...uploadedFwdRows, ...uploadedRvpRows] : revenueGridRaw,
    [uploadedFwdRows, uploadedRvpRows, revenueGridRaw],
  );
  const revenueMap     = useMemo(() => buildRevenueMap(effectiveRevenueRows), [effectiveRevenueRows]);

  const [publishAllocation, { isLoading: isPublishing }] = usePublishAllocationMutation();
  const [notes, setNotes] = useState('');

  // Margin-preview AC codes are the merchant's REAL account codes (same source the
  // backend's marginRuleBuilder matches against). Used to scope the published
  // acEnabled to real codes — never the dummy-seeded defaults that match nothing.
  const { data: marginPreview } = useGetMarginPreviewQuery(activeMerchantId, {
    skip: !activeMerchantId || forwardMode !== 'margin',
  });

  const nextVersion = merchant
    ? `v${(parseInt(String(merchant.allocationVersion || merchant.version || '0').replace(/[^0-9]/g, ''), 10) || 0) + 1}`
    : 'v1';

  const handleConfirm = async () => {
    if (!effectiveFrom) {
      enqueueSnackbar("Please set 'Effective from'.", { variant: 'warning' });
      return;
    }

    // Pre-publish OTP gate. Per the publish contract, OTP is required when the
    // merchant is Platinum (any mode) OR the allocation has loss-making lanes
    // (custom mode only). This mirrors the backend's 403 check so the user isn't
    // surprised mid-publish; the 403 fallback below is the ultimate safety net.
    // For an uploaded CSV, previewStats reflects the SAVED allocation, not the upload,
    // so the loss gate would be stale — the backend computes loss from the parsed CSV
    // and returns its 403 if OTP is needed. Skip the local loss gate in that case.
    const isPlatinum = merchant?.tier === 'Platinum';
    const lossMakingLanes = previewStats?.lossMakingLaneCount || 0;
    const lossGate = forwardMode === 'custom' && !uploadedAllocationCsv && lossMakingLanes > 0;
    if (!approvalToken && (isPlatinum || lossGate)) {
      const lossLine = lossGate
        ? `<strong>⚠ ${lossMakingLanes} lane(s) are loss-making</strong> — P1 margin below threshold.<br/><br/>`
        : '';
      const platLine = isPlatinum
        ? `<strong>⭐ Platinum client</strong> — OTP approval mandated by merchant profile.<br/><br/>`
        : '';
      dispatch(closePublishModal());
      dispatch(openOtpModal({
        title: lossGate
          ? 'Approval required: loss-making allocation'
          : 'Approval required: platinum client',
        body: `${lossLine}${platLine}Verify with the 6-digit OTP sent to the registered approver email.`,
        purpose: 'publish_lossy',
      }));
      return;
    }

    // Assemble the mode-specific publish payload. The backend contract differs per
    // mode: CUSTOM carries the full rule matrix + a CSV regenerated from the current
    // rules; CHEAPEST is rule-less and fully dynamic (no CSV); HIGHEST_PROZO_MARGIN
    // carries only the AC selection — the backend builds, ranks and merges the rules
    // from supply costs, so no CSV / customRules are sent.
    let payload;
    if (forwardMode === 'custom' && uploadedAllocationCsv) {
      // Manual upload (download-edit-upload / upload-manual): publish the uploaded
      // 39-column CSV verbatim. The backend rebuilds the rule tree from csvBlob, so no
      // UI-derived custom/reverse rules are sent — the CSV is the single source of truth.
      let csvBlob;
      try {
        csvBlob = btoa(unescape(encodeURIComponent(uploadedAllocationCsv)));
      } catch {
        enqueueSnackbar('Failed to encode the uploaded allocation CSV.', { variant: 'error' });
        return;
      }
      payload = { forwardMode: 'custom', customRules: [], reverseRules: [], csvBlob };
    } else if (forwardMode === 'custom') {
      // Pass marginLanePriority so "Highest Prozo Margin" custom rules publish in true
      // margin order (lanePriority), matching the rule editor & the on-screen preview.
      const csvRows = buildAllocationCsvRows({ autoServiceTypes, acEnabled, groupSameParent, forwardMode, groups, acMeta, acServiceTypes, costMap, revenueMap, rvpEnabled, customRules, reverseRules, marginLanePriority: marginPreview?.lanePriority || [] });
      let csvBlob;
      try {
        csvBlob = btoa(unescape(encodeURIComponent(rowsToCsv(csvRows))));
      } catch {
        enqueueSnackbar('Failed to build allocation CSV.', { variant: 'error' });
        return;
      }
      payload = { forwardMode: 'custom', customRules: toApiCustomRules(customRules), reverseRules: toApiReverseRules(reverseRules), csvBlob };
    } else if (forwardMode === 'cheapest') {
      payload = { forwardMode: 'cheapest', customRules: [], reverseRules: toApiReverseRules(reverseRules), csvBlob: null };
    } else {
      // Highest Prozo Margin: the csvBlob is the single source of truth. Build the lane
      // CSV exactly as the FE shows/downloads it — buildAllocationCsvRows uses acEnabled
      // to pick the included couriers and honours includeLossMaking (toggle OFF drops the
      // loss-making couriers per lane, ON keeps all). The backend publishes the CSV as-is,
      // so acEnabled / includeLossMaking are NOT sent — they're dead fields server-side.
      const marginCsvRows = buildAllocationCsvRows({
        autoServiceTypes, acEnabled, groupSameParent, forwardMode: 'margin', groups,
        acMeta, acServiceTypes, costMap, revenueMap, rvpEnabled,
        customRules: [], reverseRules,
        marginLanePriority: marginPreview?.lanePriority || [],
        includeLossMaking,
      });
      if (marginCsvRows.length <= 1) {
        enqueueSnackbar('No lanes to publish — enable at least one courier (Highest Prozo Margin) first.', { variant: 'warning' });
        return;
      }
      let csvBlob;
      try {
        csvBlob = btoa(unescape(encodeURIComponent(rowsToCsv(marginCsvRows))));
      } catch {
        enqueueSnackbar('Failed to build the allocation CSV.', { variant: 'error' });
        return;
      }
      payload = { forwardMode: 'highest_prozo_margin', csvBlob };
    }

    try {
      const res = await publishAllocation({
        merchantId: activeMerchantId,
        approvalToken,
        effectiveFrom: toApiIso(effectiveFrom),
        effectiveUntil: toApiIso(effectiveUntil),
        notes: notes.trim() || undefined,
        autoServiceTypes,
        groupSameParent,
        ...payload,
      }).unwrap();
      const result = res?.data ?? res;
      const publishedVersion = result?.version || nextVersion;
      enqueueSnackbar(`Published ${publishedVersion} — effective from ${effectiveFrom}`, { variant: 'success' });
      dispatch(clearApprovalToken());
      dispatch(clearUploadedAllocationCsv());
      dispatch(closePublishModal());
      dispatch(setActiveTab('log'));
    } catch (err) {
      // The backend decides OTP at publish time (Platinum tier for any mode, or
      // loss-making lanes for custom). If it demands approval and we have no token,
      // route into the OTP flow instead of surfacing a raw error.
      if (err?.data?.code === 'APPROVAL_TOKEN_REQUIRED' && !approvalToken) {
        dispatch(closePublishModal());
        dispatch(openOtpModal({
          title: 'Approval required to publish',
          body: 'This allocation needs OTP approval (Platinum client or loss-making lanes). Verify with the 6-digit OTP sent to the registered approver email.',
          purpose: 'publish_lossy',
        }));
        return;
      }
      const msg = err?.data?.message || err?.data?.error || 'Publish failed. Please try again.';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  if (!open) return null;

  // Loss-making lanes only force OTP in custom mode (per the publish contract). For an
  // uploaded CSV the saved-state count is stale, so hide it — the backend decides from
  // the parsed CSV and the uploaded-file banner already explains the verbatim publish.
  const lossMakingCount = (forwardMode === 'custom' && !uploadedAllocationCsv)
    ? (previewStats?.lossMakingLaneCount || 0)
    : 0;

  return (
    <Dialog open={open} onClose={() => dispatch(closePublishModal())} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Publish allocation rules?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Schedule when these rules should come into effect. The currently active rules will be overridden
          from the effective time onwards.
        </Typography>

        {uploadedAllocationCsv && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Publishing the uploaded allocation file{uploadedAllocationCsvName ? <> <strong>{uploadedAllocationCsvName}</strong></> : ''} verbatim — the 39-column CSV is sent as-is and the backend rebuilds the rules from it. The on-screen editor tabs are not used for this publish.
          </Alert>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <DateTimePicker
              label="Effective from"
              value={toLocalDate(effectiveFrom)}
              onChange={(d) => dispatch(setEffectiveFrom(toStoredString(d)))}
              ampm={false}
              inputFormat="dd MMM yyyy, HH:mm"
              componentsProps={{ actionBar: { actions: ['clear', 'today', 'accept'] } }}
              renderInput={(params) => (
                <TextField {...params} required fullWidth InputLabelProps={{ shrink: true }} />
              )}
            />
            <DateTimePicker
              label="Effective until (optional)"
              value={toLocalDate(effectiveUntil)}
              onChange={(d) => dispatch(setEffectiveUntil(toStoredString(d)))}
              ampm={false}
              minDateTime={toLocalDate(effectiveFrom) || undefined}
              inputFormat="dd MMM yyyy, HH:mm"
              componentsProps={{ actionBar: { actions: ['clear', 'today', 'accept'] } }}
              renderInput={(params) => (
                <TextField {...params} fullWidth InputLabelProps={{ shrink: true }} />
              )}
            />
          </Stack>
        </LocalizationProvider>

        <TextField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          placeholder="e.g. Q2 allocation — optimised for metro coverage"
          sx={{ mb: 2 }}
        />

        {lossMakingCount > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            <strong>{lossMakingCount} lane(s) are loss-making</strong> — OTP approval will be required.
          </Alert>
        )}

        {approvalToken ? (
          <Alert severity="success" sx={{ mt: 1.5, py: 0.25 }}>
            Approval token active — this publish is authorised.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => dispatch(closePublishModal())} variant="outlined" color="inherit" disabled={isPublishing}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={isPublishing}>
          {isPublishing ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {isPublishing ? 'Publishing…' : 'Confirm & publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
